import React, {
  createContext,
  useContext,
  useState,
  useRef,
  Fragment
} from 'react';
import Cookies from 'js-cookie';
import mapValues from 'lodash/mapValues';
import { useTranslation } from 'react-i18next';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';

import { analytics } from '../../utils';
import useMarketo from '../../hooks/useMarketo';
import CountryContext from '../../context/country';

export const FormContext = createContext(null);

const Form = ({
  children,
  name,
  errorMessage,
  successMessage,
  successCallback,
  trackingLabel,
  analyticsTrackMapping,
  analyticsIdentifyMapping,
  onSubmit,
  isReady,
  keepModalOpen,
  defaultFormData = null // Override the formData, f.e. if form state is kept outside (Multi Step Forms)
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [invalidFields, setInvalidFields] = useState([]);
  const [data, setFormData] = useState(null);
  const formRef = useRef(null);

  const errorFallbackMessage = t(
    'There was an error submitting your request. Please try again!'
  );
  const successFallbackMessage = t('Form was submitted successfully!');

  const id = `form_${name}`;
  const context = {
    formName: name,
    formId: id,
    isSubmitting: !isReady || isSubmitting,
    errorMessage: error,
    successMessage: success,
    invalidFields,
    formRef,
    data
  };

  const handleSubmit = e => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setInvalidFields([]);
    setSubmitting(true);

    const form = e.target;
    const formData = defaultFormData || new FormData(form);

    setFormData(formData);

    if (
      analyticsIdentifyMapping &&
      analyticsIdentifyMapping.userId &&
      analyticsIdentifyMapping.traits
    ) {
      const userId = formData.get(analyticsIdentifyMapping.userId);

      if (userId) {
        analytics.identify(
          userId,
          {
            ...mapValues(analyticsIdentifyMapping.traits, entry =>
              formData.get(entry)
            )
          },
          {
            integrations: {
              Drift: false,
              Marketo: false,
              'Marketo V2': false
            }
          }
        );
      }
    }

    const handleSuccess = () => {
      if (trackingLabel && analyticsTrackMapping) {
        analytics.track(
          trackingLabel,
          {
            category: 'Segment',
            ...mapValues(analyticsTrackMapping, entry => formData.get(entry))
          },
          {
            integrations: {
              Marketo: false,
              'Marketo V2': false
            }
          }
        );
      }

      if (formRef.current && formRef.current.reset) {
        formRef.current.reset();
      }
      setSubmitting(false);
      setSuccess(successMessage || successFallbackMessage);
      if (successCallback) {
        successCallback();
      }

      if (!keepModalOpen) {
        setTimeout(() => {
          window.dispatchEvent(new Event('closeModal'));
        }, 1000);
      }
    };

    const handleError = (errorObject, invalidFields = []) => {
      console.error('form submit failed', { errorObject, invalidFields });
      if (window && window.newrelic) {
        window.newrelic.noticeError(new Error('Form submission failed'), {
          name,
          errorObject,
          invalidFields
        });
      }

      if (invalidFields.length) {
        setInvalidFields(invalidFields);
      }

      setSubmitting(false);
      setError(errorMessage || errorFallbackMessage);
    };

    if (onSubmit) {
      onSubmit(formData, handleSuccess, handleError);
    }
  };

  return (
    <form ref={formRef} method="POST" onSubmit={handleSubmit} id={id}>
      <FormContext.Provider value={context}>{children}</FormContext.Provider>
    </form>
  );
};

const MarketoForm = DisplayComponent(
  ({
    marketoOptions: { munchkinId, baseUrl, formId, overwrites },
    ...props
  }) => {
    const [isReady, setReady] = useState(false);
    const { language } = useContext(CountryContext);
    // holder for Marketo Form object
    // https://developers.marketo.com/javascript-api/forms/api-reference/
    const marketo = useRef(null);

    useMarketo({
      baseUrl,
      munchkinId,
      formId,
      callback: form => {
        marketo.current = form;
        setReady(true);
      }
    });

    const handleSubmit = (formData, onSuccess, onError) => {
      const formValues = Array.from(formData.entries()).reduce(
        (result, [key, value]) => ({ ...result, [key]: value }),
        {}
      );

      if (overwrites) {
        Object.entries(overwrites).forEach(([key, value]) => {
          formValues[key] = value;
        });
      }

      const enrichedFormValues = {
        ...formValues,
        Language_Preference__c: language,
        Capture_URL_First__c: window.location.href,
        Capture_URL_Last__c: window.location.href
      };

      marketo.current.onSuccess(() => {
        onSuccess();
        return false; // prevent page reload
      });

      marketo.current.setValues(enrichedFormValues);
      if (marketo.current.validate()) {
        marketo.current.submit();
      } else {
        const invalidFields = marketo.current
          .getFormElem()
          .find('.mktoInvalid')
          .map((i, e) => e.id)
          .get();

        onError(null, invalidFields);
      }
    };

    return (
      <Fragment>
        <form id={`mktoForm_${formId}`} style={{ display: 'none' }} />
        <Form {...props} onSubmit={handleSubmit} isReady={isReady} />
      </Fragment>
    );
  }
);

const TFAForm = DisplayComponent(
  ({ tfaOptions: { dbFormId, dbControl, overwrites }, ...props }) => {
    const handleSubmit = (formData, onSuccess, onError) => {
      const csrftoken = Cookies.get('csrftoken');

      if (dbFormId) {
        formData.set('tfa_dbFormId', dbFormId);
      }
      if (dbControl) {
        formData.set('tfa_dbControl', dbControl);
      }
      formData.set('tfa_dbResponseId', '');
      formData.set('tfa_dbVersionId', '1');
      formData.set('tfa_switchedoff', '');

      if (overwrites) {
        Object.entries(overwrites).forEach(([key, value]) => {
          formData.set(key, value);
        });
      }

      fetch('/form_proxy/', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': csrftoken
        }
      })
        .then(res => res.json())
        .then(result => {
          if (result !== 200) {
            throw new Error('response not ok');
          }
          onSuccess();
        })
        .catch(error => {
          onError(error);
        });
    };

    return <Form {...props} onSubmit={handleSubmit} isReady />;
  }
);

export { TFAForm, TFAForm as Form, MarketoForm };
